import { useRef, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import noop from 'lodash/noop';

import Modal from '@decisiv/ui-components/lib/components/Modal';
import PlusCircle from '@decisiv/iconix/lib/components/PlusCircle';
import { useNotifications } from '@decisiv/ui-components/lib/components/Notifications';
import { createForm, FormHandler } from 'components/Form';

import useAddDepotsToContact from 'pages/Management/Contact/hooks/useAddDepotsToContact';
import parseBackendErrorMessage from 'utils/parseBackendErrorMessage';
import { useSetPrimaryDepotContactMutation } from 'pages/Management/Depot/ContactsTab/hooks/useSetPrimaryDepotContactMutation';
import ContactForm from '../ContactForm';
import DepotSelector, { ContactDepot } from '../EditContactModal/DepotSelector';
import useAddContactToDepotMutation from '../EditContactModal/hooks/useAddContactToDepotMutation';
import EditContactModal from '../EditContactModal';
import { ModalContentWrapper } from './styles';
import { ErrorMessageBanner } from '../styles';
import ExistingContactBanner from './ExistingContactBanner';

import validationSchema, { FormValues } from '../ContactForm/schema';
import applyContactValidationErrors from '../applyContactValidationErrors';
import useCreateGlobalContactMutation from './hooks/useCreateGlobalContactMutation';

const { Form, FormProvider } = createForm<FormValues>();

interface ExistingContactInfo {
  id: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  mobilePhone?: string | null;
  mobilePhoneCountryCode?: string | null;
  notes?: string | null;
  address1?: string | null;
  address2?: string | null;
  country?: string | null;
  state?: string | null;
  city?: string | null;
  postalCode?: string | null;
  notificationPreferences?: {
    emailNotificationEnabled: boolean;
    smsNotificationEnabled: boolean;
  };
  depots?: Array<{
    id: string;
    name: string;
    city?: string | null;
    state?: string | null;
    platformAccountName?: string | null;
    primaryContact: boolean;
  }>;
}

const initialValues: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  mobilePhone: '',
  emailNotificationEnabled: false,
  smsNotificationEnabled: false,
  address1: '',
  address2: '',
  country: '',
  state: '',
  city: '',
  postalCode: '',
};

interface CreateContactsModalProps {
  onClose: () => void;
  /** Overrides the `:accountId` route param; the Team page has none. */
  accountId?: string;
  forDepot?: boolean;
  depot?: {
    accountId: string;
    depotId: string;
    depotUuid: string;
    depotName: string;
    depotCity?: string;
    depotState?: string;
    depotPlatformAccountName?: string;
  };
}

const CreateContactsModal = ({
  onClose,
  accountId: accountIdProp,
  forDepot = false,
  depot = undefined,
}: CreateContactsModalProps) => {
  const formRef = useRef<FormHandler<FormValues>>(null);
  const {
    setError,
    formState: { isValid },
  } = useFormContext();
  const { accountId: accountIdParam } = useParams();
  const accountId =
    forDepot && depot ? depot.accountId : accountIdProp ?? accountIdParam;
  const { notify } = useNotifications();

  const initialDepots = useMemo<ContactDepot[]>(() => {
    if (forDepot && depot) {
      return [
        {
          id: depot.depotId,
          name: depot.depotName,
          city: depot.depotCity,
          state: depot.depotState,
          platformAccountName: depot.depotPlatformAccountName,
          isFixed: true,
        },
      ];
    }
    return [];
  }, [forDepot, depot]);

  const [depots, setDepots] = useState<ContactDepot[]>(initialDepots);
  const [savingDepots, setSavingDepots] = useState(false);
  const [primaryDepotIds, setPrimaryDepotIds] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<{
    title: string;
    description: string;
  } | null>(null);
  const [existingContact, setExistingContact] =
    useState<ExistingContactInfo | null>(null);
  const [existingContactInDepot, setExistingContactInDepot] = useState(false);
  const [editingExistingContact, setEditingExistingContact] = useState(false);

  const showCreateFailure = useCallback(
    (description: string) =>
      setErrorMessage({ title: t`Failed to create contact.`, description }),
    [],
  );

  const handlePrimaryChange = useCallback(
    (depotId: string, checked: boolean) => {
      setPrimaryDepotIds((prev) =>
        checked ? [...prev, depotId] : prev.filter((id) => id !== depotId),
      );
    },
    [],
  );

  const handleTriggerForm = async () => {
    await formRef.current?.submit();
  };

  const handleClose = useCallback(() => {
    setErrorMessage(null);
    setExistingContact(null);
    setExistingContactInDepot(false);
    setEditingExistingContact(false);
    onClose();
  }, [onClose]);

  const [addContactToDepot, { loading: addingExistingContact }] =
    useAddContactToDepotMutation();

  const handleAddExistingContact = useCallback(async () => {
    if (!existingContact || !depot) return;
    try {
      await addContactToDepot({
        variables: {
          contactId: existingContact.id,
          depotId: depot.depotId,
        },
      });
      notify({
        title: t`Contact added to depot successfully.`,
        intent: 'success',
        duration: 5000,
        onClose: noop,
      } as any);
      onClose();
    } catch (error) {
      showCreateFailure(
        parseBackendErrorMessage(error) ||
          t`An unexpected error occurred. Please try again.`,
      );
    }
  }, [
    existingContact,
    depot,
    addContactToDepot,
    notify,
    onClose,
    showCreateFailure,
  ]);

  const handleEditExistingContact = useCallback(() => {
    setEditingExistingContact(true);
  }, []);

  const addDepotsToContact = useAddDepotsToContact();
  const [setPrimaryDepotContact] = useSetPrimaryDepotContactMutation({
    refetchQueries: [
      'ManagedPlatformAccountContacts',
      'DepotContacts',
      'ActivityLogs',
    ],
  });

  const [createContact, { loading: creatingContact }] =
    useCreateGlobalContactMutation({
      onCompleted: async (data: {
        createGlobalContact?: CreateGlobalContactMutationPayload;
      }) => {
        const validationErrors = data?.createGlobalContact?.errors ?? [];
        if (validationErrors.length) {
          const handled = applyContactValidationErrors(
            validationErrors,
            setError,
          );

          const emailTaken = validationErrors.some(
            (e) => e.field === 'email' && e.code === 'taken',
          );
          if (
            emailTaken &&
            data?.createGlobalContact?.existingContact &&
            forDepot
          ) {
            setExistingContact(data.createGlobalContact.existingContact);
            setExistingContactInDepot(
              data.createGlobalContact.existingContactInDepot ?? false,
            );
          }

          if (!handled) {
            showCreateFailure(
              t`An unexpected error occurred. Please try again.`,
            );
          }
          return;
        }

        /* istanbul ignore next -- @preserve */
        if (data?.createGlobalContact?.globalContact) {
          const contactId = data.createGlobalContact.globalContact.id;

          // Add depot associations for selected depots (skip fixed depots already associated via depotId)
          const newDepots = depots.filter((d) => !d.isFixed);
          let depotErrors: string[] = [];
          if (newDepots.length > 0) {
            setSavingDepots(true);
            depotErrors = await addDepotsToContact(
              contactId,
              newDepots,
              primaryDepotIds,
            );
            setSavingDepots(false);
          }

          // Set primary for fixed depots (already associated via depotId) if marked as primary
          const fixedPrimaryDepots = depots.filter(
            (d) => d.isFixed && primaryDepotIds.includes(d.id),
          );
          await Promise.all(
            fixedPrimaryDepots.map(async (d) => {
              try {
                await setPrimaryDepotContact({
                  variables: {
                    depotId: d.id,
                    globalContactId: contactId,
                  },
                });
              } catch {
                // Primary assignment failures are non-blocking
              }
            }),
          );

          if (depotErrors.length > 0) {
            notify(
              {
                title: t`Contact created, but some depot associations failed.`,
                intent: 'caution',
                duration: 8000,
                onClose: noop,
              } as any,
              <Trans>Failed depots: {depotErrors.join(', ')}.</Trans>,
            );
          } else {
            notify({
              title: t`Contact created successfully.`,
              intent: 'success',
              duration: 5000,
              onClose: noop,
            } as any);
          }
          onClose();
        }
      },
    });

  const handleCreateContact = useCallback(
    async (data: FormValues) => {
      setErrorMessage(null);
      setExistingContact(null);
      setExistingContactInDepot(false);
      try {
        await createContact({
          variables: {
            globalContact: {
              platformAccountId: accountId as string,
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email || undefined,
              mobilePhone: data.mobilePhone || undefined,
              mobilePhoneCountryCode: data.mobilePhone
                ? data.mobilePhoneCountryCode ||
                  /* istanbul ignore next -- @preserve */ undefined
                : undefined,
              notes: data.notes || undefined,
              address1: data.address1 || undefined,
              address2: data.address2 || undefined,
              country: data.country || undefined,
              state: data.state || undefined,
              city: data.city || undefined,
              postalCode: data.postalCode || undefined,
              notificationPreferences: {
                emailNotificationEnabled:
                  data.emailNotificationEnabled || false,
                smsNotificationEnabled: data.smsNotificationEnabled || false,
              },
              depotId: forDepot && depot ? depot.depotId : '',
            },
          },
        });
      } catch (error) {
        showCreateFailure(
          parseBackendErrorMessage(error) ||
            t`An unexpected error occurred. Please try again.`,
        );
      }
    },
    [createContact, accountId, depot, forDepot, showCreateFailure],
  );

  const handleRemoveDepot = useCallback((depotId: string) => {
    setDepots((prev) => prev.filter((d) => d.id !== depotId));
  }, []);

  const isSaving = creatingContact || savingDepots || addingExistingContact;

  const depotsSection = (
    <DepotSelector
      accountId={accountId as string}
      depots={depots}
      onDepotsChange={setDepots}
      onRemoveDepot={handleRemoveDepot}
      containerSelector="#create-contact-modal"
      primaryDepotIds={primaryDepotIds}
      onPrimaryChange={handlePrimaryChange}
    />
  );

  if (editingExistingContact && existingContact) {
    return (
      <EditContactModal
        contact={{
          ...existingContact,
          email: existingContact.email ?? undefined,
          mobilePhone: existingContact.mobilePhone ?? undefined,
          mobilePhoneCountryCode:
            existingContact.mobilePhoneCountryCode ?? undefined,
          notes: existingContact.notes ?? undefined,
          address1: existingContact.address1 ?? undefined,
          address2: existingContact.address2 ?? undefined,
          country: existingContact.country ?? undefined,
          state: existingContact.state ?? undefined,
          city: existingContact.city ?? undefined,
          postalCode: existingContact.postalCode ?? undefined,
          notificationPreferences: existingContact.notificationPreferences ?? {
            emailNotificationEnabled: false,
            smsNotificationEnabled: false,
          },
          depots: existingContact.depots?.map((d) => ({
            ...d,
            city: d.city ?? undefined,
            state: d.state ?? undefined,
            platformAccountName: d.platformAccountName ?? undefined,
          })),
        }}
        onClose={handleClose}
        depot={
          depot
            ? {
                accountId: depot.accountId,
                depotId: depot.depotId,
                depotUuid: depot.depotUuid,
                depotName: depot.depotName,
              }
            : undefined
        }
      />
    );
  }

  return (
    <Modal
      actions={[
        {
          text: t`Create Contact`,
          onClick: handleTriggerForm,
          loading: isSaving,
          disabled: !isValid || isSaving,
          loadingText: t`Creating Contact...`,
        },
        {
          text: t`Cancel`,
          onClick: handleClose,
        },
      ]}
      id="create-contact-modal"
      icon={PlusCircle}
      intent="information"
      title={t`Create Contact`}
      visible
    >
      <ModalContentWrapper>
        {errorMessage && (
          <ErrorMessageBanner
            intent="danger"
            size="medium"
            title={errorMessage.title}
          >
            {errorMessage.description}
          </ErrorMessageBanner>
        )}
        {existingContact &&
          forDepot &&
          (existingContactInDepot ? (
            <ExistingContactBanner
              contact={existingContact}
              onEditExistingContact={handleEditExistingContact}
              existingContactInDepot
              loading={addingExistingContact}
            />
          ) : (
            <ExistingContactBanner
              contact={existingContact}
              onAddExistingContact={handleAddExistingContact}
              loading={addingExistingContact}
            />
          ))}
        <Form ref={formRef} onSubmit={handleCreateContact}>
          <ContactForm depotsSection={depotsSection} />
        </Form>
      </ModalContentWrapper>
    </Modal>
  );
};

const CreateContactsModalWrapper = ({
  onClose,
  accountId,
  forDepot,
  depot,
}: CreateContactsModalProps) => {
  return (
    <FormProvider
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      <CreateContactsModal
        onClose={onClose}
        accountId={accountId}
        forDepot={forDepot}
        depot={depot}
      />
    </FormProvider>
  );
};

export default CreateContactsModalWrapper;
